import { useShowToast } from '@/hooks';
import { useSubmitScoreCardMutation } from '@/store/query/ats-applications';
import { Collapse, Flex, useDisclosure, Icon, Button } from '@chakra-ui/react';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { useMemo, useRef, useState } from 'react';
import { CollectScoreCardForm } from './CollectScoreCardForm';
import {
  getInitialValues,
  getScoreCardFormConfig,
  ratingOptions,
} from './helper';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { getSelectionRatingStyle } from '@/constants/scorecard-configs';
import { SubmittedUserName } from '@/components/score-card-builder/components/submittedUserName';
import { AiButton } from '@kula-ai/components';
import { OverallEvaluationBanner } from './OverallEvaluationBanner';
import { useScoreCardAI } from '@/hooks/use-scorecardAI';
import { AIFillScorecardLoader } from '@/components/shared/scorecard';
import { KText } from '@/ui-library/components/text';
import { useScorecardAutoSaveHelper } from './useScorecardAutoSaveHelper';
import { AutoSaveLoadingIndicator } from './AutoSaveLoadingIndicator';
import { OverrideScorecardModal } from './OverrideScorecardModal';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { Button as KulaButton } from '@kula-ai/components';
import type { ScorecardType } from '@/types/interview';
import { ChevronDown, ChevronRight } from '@kula-ai/icons-core';

interface ScoreCardCollapseProps {
  scoreCard: ScorecardType;
  onSuccess?: () => void;
  canFillForOthers?: boolean;
  expandOnly?: string | number | null;
  transcript?: { transcript?: unknown[] } | null;
  interviewData?: { id?: number | string } | null;
  /** Resolved "Allow interviewers to complete scorecards with AI" setting. */
  aiFillEnabled?: boolean;
}

export const ScoreCardCollapse = ({
  scoreCard,
  onSuccess = () => {},
  canFillForOthers = false,
  expandOnly = null,
  transcript,
  interviewData,
  aiFillEnabled = true,
}: ScoreCardCollapseProps) => {
  const isMobile = useIsMobile();
  const { isOpen, onToggle, onOpen } = useDisclosure({
    defaultIsOpen: expandOnly ? +expandOnly === scoreCard?.id : true,
  });

  const {
    id,
    reviewer,
    rating,
    questions,
    feedback,
    ats_application_id,
    submitted_by,
    status,
  } = scoreCard || {};

  const isClosed = status === 'closed';

  const formRef = useRef(null);
  const ratingConfig = ratingOptions?.find((item) => item?.value === rating);
  const [submitScoreCard, { isLoading: isSubmitting }] =
    useSubmitScoreCardMutation();
  const toast = useShowToast();
  const [isEditMode, setEditMode] = useState(false);
  const currUser = useSelector(selectCurrUser);
  const canSubmit = canFillForOthers || scoreCard?.reviewer.id === currUser?.id;
  const [isAiPolling, setAiPolling] = useState(false);
  const isSomeOtherUserHasDraftScorecard =
    status === 'draft' && submitted_by?.id && submitted_by?.id !== currUser?.id;
  const [initialValues, setInitialValues] = useState(
    getInitialValues({
      isSubmitted: isSomeOtherUserHasDraftScorecard ? false : true,
      values: {
        rating,
        feedback,
        questions,
        attachments: scoreCard?.attachments || [],
        feedback_attachments: scoreCard?.feedback_attachments || [],
      },
    })
  );

  const updateInitialValues = ({
    type,
    attachments,
    attachmentKey,
    attachmentId,
  }) => {
    setInitialValues((prev) => {
      // const newValues = { ...prev };
      const newValues = { ...formRef?.current?.values };

      if (type === 'add' && attachments && attachmentKey) {
        newValues[attachmentKey] = [
          ...(prev[attachmentKey] || []),
          ...attachments,
        ];
      } else if (type === 'remove' && attachmentKey && attachmentId) {
        newValues[attachmentKey] = (prev[attachmentKey] || []).filter(
          (attachment) => attachment.id !== attachmentId
        );
      } else if (type === 'updateError' && attachments && attachmentKey) {
        // Mark the failed files with hasError flag
        const currentAttachments = prev[attachmentKey] || [];
        newValues[attachmentKey] = currentAttachments.map((file) => {
          // Check if this file is in the failed files list (match by name and size)
          const isFailed = attachments.some(
            (failedFile) =>
              failedFile.name === file.name &&
              failedFile.size === file.size &&
              file.hasError
          );
          return isFailed ? { ...file, hasError: true } : file;
        });
      }

      return newValues;
    });
  };
  const draftScActions = useDisclosure();
  const [showAiBanner, setShowAiBanner] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: ats_application_id,
    scorecardId: id,
    questions: questions,
  });

  const formConfig = useMemo(
    () =>
      getScoreCardFormConfig({
        questions,
        canSubmit,
        isEdit: isEditMode,
        initialValues,
        scoreCardDetails: scoreCard,
        canFillForOthers,
        updateInitialValues,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditMode, initialValues]
  );

  const handleAiSuccess = (scorecard, error = false) => {
    if (!error) {
      const newValues = getInitialValues({
        isSubmitted: true,
        values: scorecard,
      });

      setInitialValues(newValues);
      setShowAiBanner(true);
      // Remount so the rich-text editors re-seed from the AI-filled content.
      setFormKey((prev) => prev + 1);
    }
    setAiPolling(false);
  };

  const { handleFillWithAi, isFilling, isPolling } = useScoreCardAI({
    ats_application_id,
    scorecardId: id,
    interviewId: interviewData?.id,
    onSuccess: handleAiSuccess,
  });

  const handleSubmit = async (values) => {
    const payload = {
      rating: values?.rating,
      feedback: values?.feedback,
      questions: questions?.map((item, idx) => {
        const param = { question: item?.question };
        if (values?.[idx + 1]) param.answer = values?.[idx + 1];
        return param;
      }),
      status: 'submitted',
    };

    const result = await submitScoreCard({
      applicationId: ats_application_id,
      scorecardId: id,
      payload,
    });

    if (result?.error?.length) {
      toast({ title: 'Scorecard submission failed', status: 'error' });
    } else {
      toast({ title: 'Scorecard submitted successfully' });
      setEditMode(false);
      setShowAiBanner(false);
      onSuccess();
    }
  };

  const scorecardStyles = getSelectionRatingStyle(ratingConfig);
  // A transcript alone is not enough — "Allow interviewers to complete
  // scorecards with AI" has to be on as well (ENG-3791).
  const isFillWithAi = transcript?.transcript?.length > 0 && aiFillEnabled;
  const showFillWithAi = isFillWithAi && isEditMode && canSubmit && isOpen;

  const handleAiFill = (e) => {
    e?.stopPropagation();
    handleFillWithAi().then((success) => {
      if (success) {
        setAiPolling(true);
      }
    });
  };

  const collapseHeaderRight = () => {
    const showEdit = !isEditMode && canSubmit && !isClosed;
    return (
      <Flex gap="6px" alignItems="center" flexShrink={0}>
        {/*  ai fill button */}
        {showFillWithAi && (
          <AiButton onClick={handleAiFill} withShadow={false}>
            Fill with AI
          </AiButton>
        )}

        {/* show edit button */}
        {showEdit && (
          <KulaButton
            variant="tertiary"
            onClick={(e) => {
              if (isSomeOtherUserHasDraftScorecard) {
                draftScActions?.onOpen();
              } else {
                e?.stopPropagation();
                setEditMode(true);
                onOpen();
              }
            }}
            data-test-id="multiple-scorecard-banner-edit-icon"
            leftElement={<Edit05 size={16} />}
          >
            Edit
          </KulaButton>
        )}

        {/* collapse icon */}
        <Icon
          width="16px"
          height="16px"
          cursor="pointer"
          color="ats-primary"
          as={isOpen ? ChevronDown : ChevronRight}
        />
      </Flex>
    );
  };

  const collapseHeaderLeft = () => {
    const scorecardText = `${reviewer?.name}'s Scorecard`;
    const isCollapsed = !isOpen;
    const noRating = !rating;

    return (
      <Flex
        alignItems={isMobile ? 'flex-start' : 'center'}
        gap="12px"
        data-test-id="multiple-scorecard-banner"
        minWidth={0}
        flexShrink={1}
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Flex minWidth={0} flexShrink={1}>
          <TruncatedTextWithTooltip
            size="sm-medium"
            text={scorecardText}
            enableTooltip={true}
            textTransform="uppercase"
            color="ats-primary"
          />
        </Flex>

        {submitted_by &&
          submitted_by?.id !== reviewer?.id &&
          status === 'submitted' && (
            <SubmittedUserName
              userName={submitted_by?.name}
              userAvatar={submitted_by?.avatar}
            />
          )}

        {isCollapsed && (
          <Flex
            alignItems="center"
            cursor="pointer"
            gap="6px"
            border="1px solid"
            borderColor={isClosed ? 'secondary-200' : 'border-color'}
            p="2px 8px"
            borderRadius="xs"
            bg={isClosed ? 'secondary-50' : '#fff'}
            flexShrink={0}
          >
            {isClosed ? (
              <KText size="sm-medium" color="secondary-500">
                Closed
              </KText>
            ) : noRating ? (
              <KText size="sm-medium">Not submitted yet</KText>
            ) : (
              <Flex
                whiteSpace={isMobile ? 'normal' : 'nowrap'}
                flexWrap="wrap"
                gap="6px"
                alignItems="center"
              >
                <Icon
                  w="24px"
                  h="24px"
                  as={ratingConfig?.icon}
                  {...scorecardStyles}
                />
                <KText size="sm-medium">{ratingConfig?.label}</KText>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    );
  };

  const collapseHeader = () => {
    return (
      <Flex
        alignItems={'center'}
        onClick={onToggle}
        cursor="pointer"
        bg="ats-tertiary"
        h={isMobile ? 'auto' : '45px'}
        py={isMobile ? '8px' : '0'}
        pl={isMobile ? '12px' : '34px'}
        pr={isMobile ? '12px' : '20px'}
        mx={isMobile ? '0' : '-34px'}
        justifyContent="space-between"
        gap="12px"
        data-test-id="multiple-scorecard-banner-container"
      >
        {collapseHeaderLeft()}
        {collapseHeaderRight()}
      </Flex>
    );
  };

  return (
    <Flex
      flexDir="column"
      gap="12px"
      data-test-id="multiple-scorecard-container"
    >
      {collapseHeader()}
      <Collapse in={isOpen} unmountOnExit={false}>
        <Flex flexDir="column" gap="12px">
          {isClosed ? (
            <Flex
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              p="20px 24px"
              gap="6px"
            >
              <KText size="sm-medium" color="secondary-700">
                This review has been closed
              </KText>
              <KText size="sm" color="secondary-500" textAlign="center">
                Enough scorecards have been submitted for this activity.
              </KText>
            </Flex>
          ) : isFilling || isAiPolling || isPolling ? (
            <AIFillScorecardLoader />
          ) : (
            <CollectScoreCardForm
              formRef={formRef}
              scorecard={scoreCard}
              handleSubmit={handleSubmit}
              initialValues={initialValues}
              formConfig={formConfig}
              handleAutoSave={handleAutoSave}
              status={status}
              disabled={!(canSubmit && isEditMode)}
              ratingBanner={showAiBanner ? <OverallEvaluationBanner /> : null}
              formKey={formKey}
            />
          )}

          {isEditMode && (
            <Flex alignSelf="end" gap="12px">
              <AutoSaveLoadingIndicator isSaved={isSaved} />
              <Button
                variant="secondary"
                onClick={() => {
                  setEditMode(false);
                  setShowAiBanner(false);
                }}
                data-test-id="secondary-cta-button"
              >
                Cancel
              </Button>

              {canSubmit && (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (formRef?.current) formRef?.current?.submitForm();
                  }}
                  isLoading={isSubmitting}
                  data-test-id="primary-cta-button"
                >
                  Submit Scorecard
                </Button>
              )}
            </Flex>
          )}
        </Flex>
      </Collapse>
      {draftScActions?.isOpen && (
        <OverrideScorecardModal
          draftScActions={draftScActions}
          onCTAClick={() => {
            setEditMode(true);
            onOpen();
          }}
          scorecard={scoreCard}
        />
      )}
    </Flex>
  );
};
